import { AlertColor } from '@mui/material';
import {
  ConfigPaymentScheduleSection,
  ConfigPaymentScheduleValues,
  scheduleValuesToVenuePayment,
  validateConfigPaymentSchedule,
  venuePaymentToScheduleValues,
} from 'components/ConfigPaymentScheduleSection';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import GetQuery from 'utils/hooks/getQuery';
import { fetchFromStorage } from 'utils/storage';
import strings from '../../../../../../common/Translation/Translate';
import Notification from '../../../../../../components/Notification';
import { dataFlag } from '../../../../../../utils/constants/data';
import { routes } from '../../../../../../utils/constants/routes';
import { URLS } from '../../../../../../utils/constants/urls';
import { createDropdownData, ScrollTop } from '../../../../../../utils/validators/HelperFunctions';
import AddEdit from '../../../../CommonPage/AddEdit';
import {
  buildUnavailabilityPayload,
  mapUnavailabilityDatesFromApi,
  UnavailabilityDateRange,
} from '../Unavailability/unavailabilityDateUtils';
import { GeneralForm } from './generalForm';
import {
  hourlyPricingSelectOptions,
  isHourlyPricingValue,
  toHourlyPricingFormValue,
} from './hourlyPricingFormUtils';

type GeneralProps = {
  dropdownData?: any;
  pageResponse: any;
  getPageData: (id: number) => void;
  setPageResponse: any;
  unavailabilityDates: UnavailabilityDateRange[];
  setUnavailabilityDates: React.Dispatch<React.SetStateAction<UnavailabilityDateRange[]>>;
};

const emptySchedule = (): ConfigPaymentScheduleValues => ({
  minConfirmPaymentPercentage: '',
  paymentSlotOnePercentage: '',
  paymentSlotTwoPercentage: '',
  paymentSlotThreePercentage: '',
  paymentSlotOneBeforeDays: '',
  paymentSlotTwoBeforeDays: '',
  fullPaymentBeforeDays: '',
});

const General: FC<GeneralProps> = ({
  dropdownData,
  pageResponse,
  getPageData,
  setPageResponse,
  unavailabilityDates,
  setUnavailabilityDates,
}) => {
  const { id } = useParams() as any;
  const type = GetQuery('type');
  const Navigate = useNavigate();
  const queryStings = useLocation().search as any;
  const queryStingId = new URLSearchParams(queryStings).get('id') as string;
  const serviceChargeDropdown = JSON.parse(fetchFromStorage('serviceChargeDropdown') as any);
  const [formDefaults, setFormDefaults] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [schedule, setSchedule] = useState<ConfigPaymentScheduleValues>(emptySchedule);
  const [scheduleHasError, setScheduleHasError] = useState(false);
  const [scheduleErrors, setScheduleErrors] = useState<Partial<Record<keyof ConfigPaymentScheduleValues, string>>>(
    {},
  );
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };
  const loadConfigurationDefaults = useCallback(async () => {
    const { data } = await axiosInstance.get(URLS.venueConfigDefaults);
    setFormDefaults(data);
  }, []);

  useEffect(() => {
    if (!id && !queryStingId) {
      loadConfigurationDefaults();
    }
  }, [id, queryStingId, loadConfigurationDefaults]);

  useEffect(() => {
    const source = !id && !queryStingId ? formDefaults : pageResponse;
    if (source && !Array.isArray(source)) {
      setSchedule(venuePaymentToScheduleValues(source));
      setScheduleHasError(false);
      setScheduleErrors({});
    }
  }, [id, queryStingId, pageResponse, formDefaults]);

  const hourlyPricing = isHourlyPricingValue(pageResponse?.hourlyPricing);

  const handleScheduleChange = (field: keyof ConfigPaymentScheduleValues, value: unknown) => {
    setSchedule(prev => ({ ...prev, [field]: value }));
    if (scheduleHasError) {
      setScheduleErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const pageConfig = React.useMemo(() => {
    return {
      title: id ? strings.venueConfig_edit : strings.venueConfig_add,
      successMessage: id ? strings.common_update_message : strings.common_save_message,
      addEditApiUrl:
        id || queryStingId ? URLS.venueConfigsUpdateGeneral(id || queryStingId) : URLS.addVenueConfiguration,
      initialApi: URLS.get_edit_delete_VenueById(id || queryStingId),
      formModel: GeneralForm(
        strings,
        hourlyPricing,
        serviceChargeDropdown,
        <ConfigPaymentScheduleSection
          values={schedule}
          onChange={handleScheduleChange}
          hasError={scheduleHasError}
          errors={scheduleErrors}
        />,
      ),
      formRefreshKey: hourlyPricing ? 'hourly' : 'standard',
      navigateToPage: ``,
      isCustomResponse: true,
      queryStingId: queryStingId,
      hideClose: true,
      buttonText: 'Submit',
      scrollAfterSave: false,
    };
  }, [id, queryStingId, hourlyPricing, serviceChargeDropdown, schedule, scheduleHasError, scheduleErrors]);

  const buildGeneralRequest = (custom: any) => {
    const isHourly = isHourlyPricingValue(custom.hourlyPricing);
    return {
      venueId: custom?.venues?.value ?? custom?.venues ?? custom?.venueId,
      available: custom.available?.value ?? custom.available ?? false,
      onlyRFQ: custom.onlyRFQ?.value ?? custom.onlyRFQ ?? false,
      eventTypeId: custom.eventTypeId.value,
      tentativeBookingAllowed: custom?.tentativeBookingAllowed?.value ?? custom?.tentativeBookingAllowed ?? false,
      maxBookingCapacity: custom.maxBookingCapacity,
      minimumRevenue: isHourly ? null : custom.minimumRevenue,
      minimumDaysBeforeBooking: custom.minimumDaysBeforeBooking,
      fixedHallRental: isHourly ? null : custom.fixedHallRental || null,
      guestCountForFixedHallRental: custom.guestCountForFixedHallRental || null,
      holdDays: custom.holdDays || null,
      preLeadTime: custom.preLeadTime || 0,
      postLeadTime: custom.postLeadTime || 0,
      hourlyPricing: isHourly,
      pricePerHour: isHourly
        ? custom.pricePerHour != null && custom.pricePerHour !== ''
          ? parseFloat(String(custom.pricePerHour))
          : null
        : null,
      unavailabilityDates: buildUnavailabilityPayload(unavailabilityDates),
    };
  };

  const buildPaymentRequest = (custom: any) => {
    const serviceChargePercent = custom.serviceCharge?.value ?? custom.serviceChargePercentage;
    return {
      bookingConfirmationBy: custom.bookingConfirmationBy?.value,
      serviceChargePercentage: serviceChargePercent != null ? parseFloat(String(serviceChargePercent)) : null,
      serviceCharge: custom.serviceCharge?.value || null,
      ...scheduleValuesToVenuePayment(schedule),
    };
  };

  const handleCustomResponse = (custom: any) => buildGeneralRequest(custom);

  const customCallbackResponse = (callbackValue: any) => {
    if (!id) {
      setUnavailabilityDates([]);
      Navigate(`${routes.addVenueConfiguration}?id=${callbackValue.id}`);
    } else {
      type === 'copy' && Navigate(routes.venueConfiguration);
    }
    getPageData(callbackValue.id);
  };
  const checkUnavailableDate = async (id: number) => {
    try {
      setLoading(true);
      const { data, status } = await axiosInstance.get(URLS.checkUnavailablityDate(id));
      if (status === 200) {
        setUnavailabilityDates(mapUnavailabilityDatesFromApi(data));
      }
    } catch (e: any) {
    } finally {
      setLoading(false);
    }
  };
  const dropdownValue = {
    available: createDropdownData(dataFlag(strings), ['id', 'name']),
    onlyRFQ: createDropdownData(dataFlag(strings), ['id', 'name']),
    hourlyPricing: hourlyPricingSelectOptions(strings),
    eventTypeId: createDropdownData(dropdownData.eventTypes, ['id', 'name']),
    tentativeBookingAllowed: createDropdownData(dataFlag(strings), ['id', 'name']),
    venues: createDropdownData(dropdownData.venues, ['id', 'name']),
    bookingConfirmationBy: createDropdownData(dropdownData.bookingConfirmationBy, ['id', 'name']),
    serviceCharge: createDropdownData(dropdownData.serviceCharges, ['id', 'name']),
  };

  const formValues = useMemo(() => {
    const isNew = !id && !queryStingId;
    const currentValues = Array.isArray(pageResponse) ? {} : pageResponse ?? {};
    const base = isNew ? { ...(formDefaults ?? {}), ...currentValues } : currentValues;
    const holdDays = base?.holdDays;
    const minimumDaysBeforeBooking =
      base?.minimumDaysBeforeBooking != null && base?.minimumDaysBeforeBooking !== ''
        ? base.minimumDaysBeforeBooking
        : isNew && holdDays != null
          ? holdDays
          : base?.minimumDaysBeforeBooking;
    return {
      ...base,
      minimumDaysBeforeBooking,
      hourlyPricing: toHourlyPricingFormValue(base?.hourlyPricing, strings),
    };
  }, [id, queryStingId, pageResponse, formDefaults]);

  const handlePropsChange = (field: string, value: any, _formData: any) => {
    if (field === 'venues') {
      checkUnavailableDate(value.value);
    }
    if (field === 'hourlyPricing') {
      const isHourly = isHourlyPricingValue(value);
      setPageResponse((prev: any) => ({
        ...prev,
        hourlyPricing: value,
        ...(isHourly
          ? {
              minimumRevenue: '',
              fixedHallRental: '',
              minimumRevenuePrev: prev.minimumRevenue,
              fixedHallRentalPrev: prev.fixedHallRental,
              pricePerHour: prev.pricePerHour ?? '',
            }
          : {
              minimumRevenue: prev.minimumRevenuePrev ?? prev.minimumRevenue,
              fixedHallRental: prev.fixedHallRentalPrev ?? prev.fixedHallRental,
              pricePerHour: '',
            }),
      }));
      return;
    }
    setPageResponse((prev: any) => ({ ...prev, [field]: value }));
  };

  const customHandleSaveHelper = async (body: any, _e: any) => {
    const validation = validateConfigPaymentSchedule(schedule);
    if (!validation.valid) {
      setScheduleHasError(true);
      setScheduleErrors(validation.errors);
      setMessage({
        display: true,
        severity: 'error',
        message: strings.required_error_prefix_message
          ? `${strings.required_error_prefix_message} ${Object.keys(validation.errors).join(', ')}`
          : Object.values(validation.errors).join(', '),
      });
      ScrollTop(0);
      return;
    }

    try {
      const generalRequest = buildGeneralRequest(body);
      const paymentRequest = buildPaymentRequest(body);
      setLoading(true);
      let configId = id || queryStingId;
      if (configId) {
        await axiosInstance.put(URLS.venueConfigsUpdateGeneral(configId), generalRequest);
        await axiosInstance.put(URLS.venueConfigsUpdatePayment(configId), paymentRequest);
      } else {
        const { data } = await axiosInstance.post(URLS.addVenueConfiguration, generalRequest);
        configId = data.id;
        await axiosInstance.put(URLS.venueConfigsUpdatePayment(configId), paymentRequest);
      }
      const data = { id: configId };
      setMessage({
        display: true,
        severity: 'success',
        message: pageConfig.successMessage,
      });
      setTimeout(() => {
        customCallbackResponse(data);
      }, 1000);
    } catch (error: any) {
      ScrollTop(0);
      setLoading(false);
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description
            ? error.response.data.error_description
            : error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const customHandleSave = (body: any, e: any) => {
    customHandleSaveHelper(body, e);
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <AddEdit
        pageLoading={loading}
        dropdownValue={dropdownValue}
        pageConfig={pageConfig}
        handlePropsChange={handlePropsChange}
        pageResponse={formValues}
        handleCustomResponse={handleCustomResponse}
        customHandleSave={customHandleSave}>
      </AddEdit>
    </>
  );
};
export default General;
